"use client";
import { StarIcon } from "../Icons";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { getIp } from "@/utils/getCountryIp";
import { useCartStore2 } from "@/stores/cartStore";
import { toast } from "@/components/ui/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { v4 as uuidv4 } from "uuid";
import axiosInstance from "@/utils/axiosInstance";
import useAuthStore from "@/stores/useAuthStore";
import { usePathname } from "next/navigation";
import { getFbc } from "@/utils/getFbcMeta";
import { getFbp } from "@/utils/getFbpMeta";
import { getContentIds } from "@/utils/metaContentIds";

interface PlansProps {
  products: any[];
}

export default function Plans({ products }: PlansProps) {
  const router = useRouter();
  const [country, setCountry] = useState<string | null>(null);
  const [showAllFeaturesFirstPlan, setShowAllFeaturesFirstPlan] = useState(false);
  const [showAllFeaturesSecondPlan, setShowAllFeaturesSecondPlan] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProductId, setLoadingProductId] = useState<number | null>(null);
  const { addItem, cart2 } = useCartStore2();
  const { userMetadata, userData } = useAuthStore();
  const pathname = usePathname();
  const hasTracked = useRef(false);
  const eventIdRef = useRef<string | null>(null);
  if (!eventIdRef.current) {
    eventIdRef.current = uuidv4();
  }
  
  useEffect(() => {
    if (hasTracked.current || pathname !== "/planes") return;
    hasTracked.current = true;

    const eventId = eventIdRef.current!;
    const contentIds = products.map((p) => `product-${p.id}`);
    const total = products.reduce(
      (acc, p) => acc + (p.discounted_price ?? p.price),
      0
    );

 
    const value = Number(total);
    const currency = "CLP";

    // Pixel: ViewContent
    if (typeof window.fbq === "function") {
      window.fbq("track", "ViewContent", {
        content_ids: contentIds,
        content_type: "product",
        value,
        currency,
        event_id: eventId,
        content_name: "Planes de entrenamiento",
        
      });

      //console.log("ViewContent enviado desde /planes");
    }

    // CAPI
    axiosInstance.post(`${process.env.NEXT_PUBLIC_API_URL}/meta/view-content`, {
      event_id: eventId,
      userData: {
        ...(userData?.email && { email: userData.email }),
        ...(userMetadata?.first_name && { first_name: userMetadata.first_name }),
        ...(userMetadata?.last_name && { last_name: userMetadata.last_name }),
        ...(getFbc() && { fbc: getFbc() }),
        ...(getFbp() && { fbp: getFbp() }),
      },
      content: {
        content_ids: contentIds,
        value,
        currency,
        content_type: "product",
        content_name: "Planes de entrenamiento",
      },
      action_source: "website"
    });

    //console.log(
      //"✅ ViewContent enviado desde /planes",
      //{ contentIds, total },
      //getFbc(),
      //getFbp()
    //);
  }, [pathname, products, userMetadata]);

  const selectPlan = async (product: any) => {
    if (isLoading) return;
    setIsLoading(true);
    setLoadingProductId(product.id);
    const eventId = uuidv4();

    const existingSubscription = cart2?.items?.find(
      (item: any) => item.line_type === "subscription"
    );

    const contentIds = getContentIds([
      {
        productId: product.id,
        qty: 1,
      },
    ]);

    const value = Number(product.price);
    const currency = "CLP";

    try {
      if (typeof window.fbq === "function") {
        window.fbq("track", "AddToCart", {
          content_name: product.name,
          content_ids: contentIds,
          content_type: "product",
          value,
          currency, 
          event_id: eventId,
        });
      }

      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "add_to_cart",
          event_id: eventId,
          ecommerce: {
            currency: currency,
            value: value,
            items: [
              {
                item_id: product.id,
                item_name: product.name,
                price: product.discounted_price ?? product.price,
                quantity: 1,
              },
            ],
          },
        });

        const datalayer = {
          value: value,
          currency: currency,
          items: [
            {
              item_id: product.id,
              item_name: product.name,
              price: product.discounted_price ?? product.price,
              quantity: 1,
            },
          ],
        };

        //console.log("Envio add to cart datalayer", datalayer);
      }

      axiosInstance.post(`${process.env.NEXT_PUBLIC_API_URL}/meta/add-to-cart`, {
        event_id: eventId,
        userData: {
          ...(userData?.email && { email: userData.email }),
          ...(userMetadata?.first_name && { first_name: userMetadata.first_name }),
          ...(userMetadata?.last_name && { last_name: userMetadata.last_name }),
          ...(getFbc() && { fbc: getFbc() }),
          ...(getFbp() && { fbp: getFbp() }),
        },
        product: {
          content_ids: contentIds,
          content_name: product.name,
          value,
          currency,
          content_type: "product",
        },
        action_source: "website"
      });

      await addItem({
        line_type: "subscription",
        product_id: product.id,
        quantity: 1
      });

      toast({
        title: "Plan añadido al carrito",
        description: `Has seleccionado el plan ${product.name}.`,
        duration: 3000,
      });

      router.push("/payment");
    } catch (e) {
      toast({
        title: "Error",
        description: "No pudimos procesar tu selección. Intenta nuevamente.",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
      setLoadingProductId(null);
    }
  };

  // Constante con los beneficios del plan
  const planBenefits = [
    "Plan de Entrenamiento",
    "Plan Nutricional personalizado",
    "Recetas saludables",
    "Acceso ilimitado a la plataforma",
    "Clases On Demand",
    "Acceso a más de 500 clases/programas",
    "Guarda tus clases favoritas",
    "Descarga de clases (modo offline desde la app)",
    "Desafíos (desbloquea clase a clase a medida que las realizas)",
    "Dispositivos Multiplataforma",
    "Entrena donde y cuando quieras",
    "Soporte online"
  ];

  // Número de beneficios a mostrar inicialmente
  const initialBenefitsCount = 4;

  useEffect(() => {
    const initializeCountry = async () => {
      //const resIp = await getIp();

      setCountry("CL");
    };

    initializeCountry();
  }, []);

  return (
    <>
      <div className="flex justify-center items-center flex-col px-5 text-center gap-8 mt-20">
        {
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div className="relative border-2 border-deep-purple rounded-3xl order-2 md:order-1">
              <div className="px-5 py-16">
                <span className="font-black text-3xl md:text-6xl block">
                  $
                  {country === "CL" || country === null
                    ? products[1].price.toLocaleString("es-CL")
                    : products[1].usd_price}
                </span>
                <span className="font-medium text-2xl">MENSUAL</span>
              </div>
              <div className="bg-orange py-2 text-cream inline-flex w-full justify-center gap-5">
                <StarIcon className="w-8 h-8 fill-cream" />
                <span className="text-2xl">PLAN MENSUAL</span>
                <StarIcon className="w-8 h-8 fill-cream" />
              </div>
              <div className="p-10">
                <span className="font-bold text-2xl">INCLUYE</span>
                <ul className="mt-5 text-left text-lg font-light list-disc [&>*]:ml-5">
                  {planBenefits.slice(0, showAllFeaturesFirstPlan ? planBenefits.length : initialBenefitsCount).map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                  
                  <button 
                    onClick={() => setShowAllFeaturesFirstPlan(!showAllFeaturesFirstPlan)}
                    className="text-bright-pink hover:text-deep-purple font-medium mt-2 flex items-center"
                  >
                    {showAllFeaturesFirstPlan ? "Ver menos" : "Ver más beneficios..."}
                  </button>
                </ul>
              </div>
              <div className="p-5">
                <button
                  onClick={() => selectPlan(products[1])}
                  disabled={isLoading}
                  aria-busy={isLoading && loadingProductId === products[1].id}
                 className="px-8 py-3 bg-transparent text-deep-purple font-bold border-2 border-deep-purple rounded-full hover:bg-deep-purple hover:border-deep-purple hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"	
                >
                  {isLoading && loadingProductId === products[1].id ? (
                    <span className="inline-flex items-center gap-2">
                      <span>Procesando...</span>
                      <span className="animate-spin rounded-full h-4 w-4 border border-t-transparent border-deep-purple"></span>
                    </span>
                  ) : (
                    "LO QUIERO AHORA"
                  )}
                </button>
              </div>
            </div>

            
            <div className="relative border-2 border-deep-purple rounded-3xl order-1 md:order-2">
              <div className="absolute bg-bright-pink w-fit px-2 md:px-6 py-2 rounded-full text-white -top-12 left-1/2 -translate-x-1/2 shadow-md font-bold">
                <span>MEJOR PRECIO</span>
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 bg-deep-purple w-4/5 mx-auto rounded-b-xl py-0.5">
                <span className="text-cream font-medium">
                  AHORRAS{" "}
                  {country === "CL" || country === null
                    ? "CLP $60.000"
                    : "USD $60"}
                </span>
              </div>
              <div className="px-5 py-12">
               
              <del className="text-xl font-bold text-deep-purple/70 line-through">
                  $
                  {country === "CL" || country === null
                    ? "9.990"
                    : "10"}
                </del> 
               
             
                <span className="font-black text-3xl md:text-6xl block">
                  $
                  {country === "CL" || country === null
                    ? Math.round(products[0].price / 12).toLocaleString("es-CL")
                    : Math.round((products[0].usd_price ?? 0) / 12)}
                </span>
                <span className="font-medium text-2xl">MENSUAL</span>
                <span className="block">Pagas 12 meses por adelantado</span>
              </div>
              <div className="bg-bright-pink py-2 text-cream inline-flex w-full justify-center gap-5">
                <StarIcon className="w-8 h-8 fill-cream" />
                <span className="text-2xl">PLAN ANUAL</span>
                <StarIcon className="w-8 h-8 fill-cream" />
              </div>
              <div className="p-10">
                <span className="font-bold text-2xl">INCLUYE</span>
                <ul className="mt-5 text-left text-lg font-light list-disc [&>*]:ml-5">
                  {planBenefits.slice(0, showAllFeaturesSecondPlan ? planBenefits.length : initialBenefitsCount).map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                  
                  <button 
                    onClick={() => setShowAllFeaturesSecondPlan(!showAllFeaturesSecondPlan)}
                    className="text-bright-pink hover:text-deep-purple font-medium mt-2 flex items-center"
                  >
                    {showAllFeaturesSecondPlan ? "Ver menos" : "Ver más beneficios..."}
                  </button>
                </ul>
              </div>
              <div className="p-5">
                <button
                  onClick={() => selectPlan(products[0])}
                  disabled={isLoading}
                  aria-busy={isLoading && loadingProductId === products[0].id}
                  className="px-8 py-3 bg-transparent text-deep-purple font-bold border-2 border-deep-purple rounded-full hover:bg-deep-purple hover:border-deep-purple hover:text-white transition-all duration-300 shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed "	
                >
                  {isLoading && loadingProductId === products[0].id ? (
                    <span className="inline-flex items-center gap-2">
                      <span>Procesando...</span>
                      <span className="animate-spin rounded-full h-4 w-4 border border-t-transparent border-deep-purple"></span>
                    </span>
                  ) : (
                    "LO QUIERO AHORA"
                  )}
                </button>
              </div>
            </div>
          </div>
        }
      </div>
      <Toaster />
    </>
  );
}
